import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatBytes, formatPhysicalSize, getPatternMetrics } from '../config';
import { drawPattern } from '../renderer';
import type { CabinetPoint, GeneratorConfig, PowerRoute } from '../types';

interface PreviewProps {
  config: GeneratorConfig;
  updateConfig: (updater: (current: GeneratorConfig) => GeneratorConfig) => void;
}

interface Point {
  x: number;
  y: number;
}

export function Preview({ config, updateConfig }: PreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointerPositions = useRef(new Map<number, Point>());
  const dragOrigin = useRef<Point | null>(null);
  const previousPinchDistance = useRef<number | null>(null);
  const drawingPointerOrigin = useRef<Point | null>(null);
  const [viewport, setViewport] = useState({ width: 1, height: 1 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [cursor, setCursor] = useState<Point | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [drawingPower, setDrawingPower] = useState(false);
  const [draftPoints, setDraftPoints] = useState<CabinetPoint[]>([]);

  const draftRoute = useMemo<PowerRoute | null>(
    () =>
      draftPoints.length > 0
        ? {
            id: '__draft__',
            points: draftPoints,
            color: config.power.lineColor,
            lineWidth: config.power.lineWidth,
            incoming: config.power.incoming,
            cableLabel: config.power.cableLabel,
          }
        : null,
    [config.power, draftPoints],
  );

  const renderedConfig = useMemo(
    () =>
      draftRoute
        ? {
            ...config,
            power: { ...config.power, routes: [...config.power.routes, draftRoute] },
          }
        : config,
    [config, draftRoute],
  );

  const metricsResult = useMemo(() => {
    try {
      return { metrics: getPatternMetrics(config), error: null };
    } catch (error) {
      return { metrics: null, error: error instanceof Error ? error.message : 'Invalid settings.' };
    }
  }, [config]);

  const fitScale = useMemo(() => {
    if (!metricsResult.metrics) return 1;
    const availableWidth = Math.max(1, viewport.width - 48);
    const availableHeight = Math.max(1, viewport.height - 48);
    return Math.min(
      availableWidth / metricsResult.metrics.resolution.width,
      availableHeight / metricsResult.metrics.resolution.height,
    );
  }, [metricsResult.metrics, viewport]);

  const renderedScale = Math.max(0.0001, fitScale * zoom);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setViewport({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const metrics = metricsResult.metrics;
    if (!canvas || !metrics) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(viewport.width * dpr));
    canvas.height = Math.max(1, Math.round(viewport.height * dpr));
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) return;

    const frame = window.requestAnimationFrame(() => {
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      const originX = viewport.width / 2 - (metrics.resolution.width * renderedScale) / 2 + pan.x;
      const originY = viewport.height / 2 - (metrics.resolution.height * renderedScale) / 2 + pan.y;
      context.translate(originX, originY);
      context.scale(renderedScale, renderedScale);
      void drawPattern(context, renderedConfig, { clear: false }).catch(() => undefined);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [metricsResult.metrics, pan, renderedConfig, renderedScale, viewport]);

  useEffect(() => {
    const onFullscreenChange = () =>
      setFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const zoomAt = useCallback(
    (factor: number, point: Point) => {
      const nextZoom = clamp(zoom * factor, 0.1, 80);
      if (nextZoom === zoom) return;
      const center = { x: viewport.width / 2, y: viewport.height / 2 };
      const ratio = nextZoom / zoom;
      setPan({
        x: point.x - center.x - (point.x - center.x - pan.x) * ratio,
        y: point.y - center.y - (point.y - center.y - pan.y) * ratio,
      });
      setZoom(nextZoom);
    },
    [pan, viewport, zoom],
  );

  const toPatternPoint = useCallback(
    (point: Point): Point | null => {
      const metrics = metricsResult.metrics;
      if (!metrics) return null;
      const originX = viewport.width / 2 - (metrics.resolution.width * renderedScale) / 2 + pan.x;
      const originY = viewport.height / 2 - (metrics.resolution.height * renderedScale) / 2 + pan.y;
      const x = (point.x - originX) / renderedScale;
      const y = (point.y - originY) / renderedScale;
      if (x < 0 || y < 0 || x >= metrics.resolution.width || y >= metrics.resolution.height) {
        return null;
      }
      return { x: Math.floor(x), y: Math.floor(y) };
    },
    [metricsResult.metrics, pan, renderedScale, viewport],
  );

  const toCabinetPoint = useCallback(
    (point: Point): CabinetPoint | null => {
      if (config.mode !== 'led') return null;
      const patternPoint = toPatternPoint(point);
      if (!patternPoint) return null;
      return {
        column: Math.floor(patternPoint.x / config.led.cabinetResolution.width),
        row: Math.floor(patternPoint.y / config.led.cabinetResolution.height),
      };
    },
    [config.led.cabinetResolution, config.mode, toPatternPoint],
  );

  const finishPowerRoute = useCallback(() => {
    const minimumPoints = config.power.incoming ? 1 : 2;
    if (draftPoints.length < minimumPoints) return;
    const route: PowerRoute = {
      id:
        typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `route-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      points: draftPoints,
      color: config.power.lineColor,
      lineWidth: config.power.lineWidth,
      incoming: config.power.incoming,
      cableLabel: config.power.cableLabel.trim(),
    };
    updateConfig((current) => ({
      ...current,
      power: { ...current.power, routes: [...current.power.routes, route] },
    }));
    setDraftPoints([]);
    setDrawingPower(false);
  }, [config.power, draftPoints, updateConfig]);

  const cancelPowerRoute = useCallback(() => {
    setDraftPoints([]);
    setDrawingPower(false);
  }, []);

  useEffect(() => {
    if (!drawingPower) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelPowerRoute();
      if (event.key === 'Enter') finishPowerRoute();
      if ((event.key === 'Backspace' || event.key === 'Delete') && draftPoints.length > 0) {
        event.preventDefault();
        setDraftPoints((current) => current.slice(0, -1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cancelPowerRoute, draftPoints.length, drawingPower, finishPowerRoute]);

  const localPoint = (event: React.PointerEvent | React.WheelEvent): Point => {
    const bounds = containerRef.current!.getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  };

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const centerView = () => {
    setPan({ x: 0, y: 0 });
  };

  const showActualPixels = () => {
    setZoom(clamp(1 / fitScale, 0.1, 80));
    setPan({ x: 0, y: 0 });
  };

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await containerRef.current?.requestFullscreen();
  };

  return (
    <section className="preview-shell" aria-label="Live pattern preview">
      <div className="preview-toolbar">
        <div className="preview-status">
          {metricsResult.metrics ? (
            <>
              <strong>
                {metricsResult.metrics.resolution.width} × {metricsResult.metrics.resolution.height}
              </strong>
              <span>{formatPhysicalSize(metricsResult.metrics.physical, config.displayUnit)}</span>
              <span>RGBA ≈ {formatBytes(metricsResult.metrics.estimatedBytes)}</span>
            </>
          ) : (
            <strong className="error-text">{metricsResult.error}</strong>
          )}
        </div>
        <div className="preview-actions">
          {config.mode === 'led' && (
            <>
              <button
                type="button"
                className={`tool-button tool-button--wide cable-tool ${drawingPower ? 'is-active' : ''}`}
                aria-pressed={drawingPower}
                title="Draw a cabinet power route"
                onClick={() => {
                  if (drawingPower) cancelPowerRoute();
                  else setDrawingPower(true);
                }}
              >
                Cable
              </button>
              {drawingPower && (
                <>
                  <button
                    type="button"
                    className="tool-button tool-button--wide"
                    disabled={draftPoints.length === 0}
                    onClick={() => setDraftPoints((current) => current.slice(0, -1))}
                  >
                    Undo
                  </button>
                  <button
                    type="button"
                    className="tool-button tool-button--wide tool-button--confirm"
                    disabled={draftPoints.length < (config.power.incoming ? 1 : 2)}
                    onClick={finishPowerRoute}
                  >
                    Finish
                  </button>
                  <button
                    type="button"
                    className="tool-button tool-button--wide"
                    onClick={cancelPowerRoute}
                  >
                    Cancel
                  </button>
                </>
              )}
            </>
          )}
          <button
            type="button"
            className="tool-button"
            onClick={() => zoomAt(1.25, center(viewport))}
          >
            +<span className="sr-only">Zoom in</span>
          </button>
          <button
            type="button"
            className="tool-button"
            onClick={() => zoomAt(0.8, center(viewport))}
          >
            −<span className="sr-only">Zoom out</span>
          </button>
          <button type="button" className="tool-button tool-button--wide" onClick={resetView}>
            Fit
          </button>
          <button type="button" className="tool-button tool-button--wide" onClick={centerView}>
            Center
          </button>
          <button
            type="button"
            className="tool-button tool-button--wide"
            onClick={showActualPixels}
          >
            100%
          </button>
          <button
            type="button"
            className="tool-button tool-button--wide"
            onClick={toggleFullscreen}
          >
            {fullscreen ? 'Exit' : 'Full'}
          </button>
        </div>
      </div>
      <div
        ref={containerRef}
        className={`preview-stage ${drawingPower ? 'is-drawing-cable' : ''}`}
        onWheel={(event) => {
          event.preventDefault();
          zoomAt(event.deltaY < 0 ? 1.12 : 0.89, localPoint(event));
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          const point = localPoint(event);
          if (drawingPower) {
            drawingPointerOrigin.current = point;
            return;
          }
          pointerPositions.current.set(event.pointerId, point);
          dragOrigin.current = point;
          if (pointerPositions.current.size === 2) {
            previousPinchDistance.current = distance([...pointerPositions.current.values()]);
          }
        }}
        onPointerMove={(event) => {
          const point = localPoint(event);
          setCursor(toPatternPoint(point));
          if (drawingPower) return;
          if (!pointerPositions.current.has(event.pointerId)) return;
          pointerPositions.current.set(event.pointerId, point);
          const points = [...pointerPositions.current.values()];
          if (points.length === 2) {
            const nextDistance = distance(points);
            const previousDistance = previousPinchDistance.current;
            if (previousDistance) zoomAt(nextDistance / previousDistance, midpoint(points));
            previousPinchDistance.current = nextDistance;
            return;
          }
          if (dragOrigin.current) {
            const delta = { x: point.x - dragOrigin.current.x, y: point.y - dragOrigin.current.y };
            setPan((current) => ({ x: current.x + delta.x, y: current.y + delta.y }));
            dragOrigin.current = point;
          }
        }}
        onPointerUp={(event) => {
          if (drawingPower) {
            const start = drawingPointerOrigin.current;
            const point = localPoint(event);
            drawingPointerOrigin.current = null;
            if (start && Math.hypot(point.x - start.x, point.y - start.y) <= 8) {
              const cabinet = toCabinetPoint(point);
              if (cabinet) {
                setDraftPoints((current) => {
                  const previous = current[current.length - 1];
                  if (previous?.column === cabinet.column && previous.row === cabinet.row) {
                    return current;
                  }
                  return [...current, cabinet];
                });
              }
            }
            return;
          }
          pointerPositions.current.delete(event.pointerId);
          previousPinchDistance.current = null;
          dragOrigin.current = null;
        }}
        onPointerCancel={(event) => {
          drawingPointerOrigin.current = null;
          pointerPositions.current.delete(event.pointerId);
          previousPinchDistance.current = null;
          dragOrigin.current = null;
        }}
        onPointerLeave={() => setCursor(null)}
      >
        <canvas ref={canvasRef} />
        {drawingPower && (
          <div className="drawing-help">
            <strong>
              {draftPoints.length === 0 ? 'Choose the first cabinet' : 'Add the next cabinet'}
            </strong>
            <span>Enter: finish · Backspace: undo · Esc: cancel</span>
          </div>
        )}
        {cursor && (
          <output className="cursor-readout">
            {drawingPower && config.mode === 'led'
              ? `Cabinet C${Math.floor(cursor.x / config.led.cabinetResolution.width) + 1} · R${Math.floor(cursor.y / config.led.cabinetResolution.height) + 1}`
              : `X ${cursor.x} · Y ${cursor.y}`}
          </output>
        )}
        <div className="zoom-readout">{Math.round(renderedScale * 100)}%</div>
      </div>
    </section>
  );
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function center(viewport: { width: number; height: number }): Point {
  return { x: viewport.width / 2, y: viewport.height / 2 };
}

function distance(points: Point[]): number {
  const [first, second] = points;
  if (!first || !second) return 0;
  return Math.hypot(first.x - second.x, first.y - second.y);
}

function midpoint(points: Point[]): Point {
  const [first, second] = points;
  if (!first || !second) return { x: 0, y: 0 };
  return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
}
